import React, { useEffect, useState, useRef } from 'react';
import { Search, X, AlertTriangle } from 'lucide-react';
import { agentApi } from '../../services/api';
import { Button, Chip, Input } from '../../components/ui';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';
import { StepTemplateMetadata } from './StepTemplateMetadata.js';
import type { AgentTemplateSummary, AgentTemplateSearchResult } from '../../generated/api/types.gen.js';

export type TemplateSearchResult = AgentTemplateSearchResult;

interface StepTemplatePickerProps {
  step: WorkflowBuilderStep;
  /** Optional local roles list used only to resolve names for the current selection */
  roles?: AgentTemplateSummary[];
  onSelect: (result: TemplateSearchResult | null) => void;
}

export const StepTemplatePicker: React.FC<StepTemplatePickerProps> = ({ step, roles, onSelect }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<TemplateSearchResult[] | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentRole = roles?.find((r) => r.id === step.templateId || r.slug === step.templateSlug);
  const currentRoleDisabled = Boolean(
    step.templateSlug && roles?.some((r) => (r.id === step.templateId || r.slug === step.templateSlug) && r.disabled),
  );

  useEffect(() => {
    if (!search.trim()) {
      setResults(null);
      return;
    }
    const timer = setTimeout(() => {
      agentApi
        .searchTemplates(search, 10)
        .then((data) => {
          // Results live at `data.data` (SearchAgentTemplatesResponses in types.gen.ts) — see the
          // same fix in StepConfigTab.tsx's identical search effect.
          setResults(data.data ?? []);
          setOpen(true);
        })
        .catch(() => setResults(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (r: TemplateSearchResult) => {
    onSelect(r);
    setSearch('');
    setResults(null);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-2 top-2 text-faint" />
        <Input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => {
            if (results) setOpen(true);
          }}
          className={`pl-7 pr-9 ${step.templateSlug && currentRoleDisabled ? 'border-warning' : ''}`}
        />
        {search && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Clear search"
            className="absolute right-1 top-0.5 size-7"
            onClick={() => {
              setSearch('');
              setResults(null);
              setOpen(false);
            }}
          >
            <X size={14} />
          </Button>
        )}
      </div>

      {step.templateSlug && (
        <div className="mt-1 flex items-center gap-1.5 text-xs">
          <span className="text-muted">Selected:</span>
          <span className="text-heading font-medium">{currentRole?.title ?? step.templateSlug}</span>
          {currentRole?.departmentId && <span className="text-faint">({currentRole.departmentId})</span>}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Clear selection"
            title="Clear selection"
            className="size-6"
            onClick={() => onSelect(null)}
          >
            <X size={12} />
          </Button>
        </div>
      )}
      <StepTemplateMetadata step={step} template={currentRole} />

      {currentRoleDisabled && (
        <div className="flex items-center gap-1 mt-1 text-warning text-xs">
          <AlertTriangle size={12} />
          <span>This template is disabled. The workflow cannot be activated until this is resolved.</span>
        </div>
      )}

      {open && results && results.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-surface border rounded-lg shadow-lg max-h-48 overflow-auto">
          {results.map((r) => (
            <div
              key={r.id}
              onClick={() => select(r)}
              className={`px-3 py-2 hover:bg-surface-hover cursor-pointer text-sm border-b border-border last:border-b-0 ${
                step.templateSlug === r.slug ? 'bg-surface-alt' : ''
              }`}
            >
              <div className="text-heading font-medium">{r.name}</div>
              <div className="flex items-center gap-2 text-xs text-muted mt-0.5">
                {r.departmentId && <span>{r.departmentId}</span>}
                {r.platform && <Chip>{r.platform}</Chip>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && results && results.length === 0 && search.trim() && (
        <div className="absolute z-10 w-full mt-1 bg-surface border rounded-lg shadow-lg p-3 text-sm text-muted">
          No templates found for "{search}"
        </div>
      )}
    </div>
  );
};
